import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, TextInput, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import { isMobileBrowser, isWeb } from 'shared/constants/platform';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

import NumberPadButton, { DELETE } from './NumberPadButton';

export interface NumberPadProps extends PressableAnalyticsProps {
  /**
   * For showing loading state on submit button
   */
  submitButtonLoading?: boolean;
  /**
   * Label for the submit button
   */
  submitButtonLabel: string;
  /**
   * Optional stable identifier for the submit button.
   */
  submitButtonTestID?: string;
  /**
   * optional boolean used for disabling the entire component
   */
  disabled?: boolean;
  /**
   * optional boolean used for disabling the submit button
   */
  submitDisabled?: boolean;
  /**
   * optional boolean used for showing the submit button
   */
  showSubmitButton?: boolean;
  /**
   *
   * @returns void
   * callback to handle submitting
   */
  handleSubmit: () => void;
  /**
   *
   * @param s string inserted at component press/click
   * @returns void
   * callback for handling the press of a singular number button
   */
  handleButtonPress: (s: string) => void;
  showsDoubleZero?: boolean;
  /**
   * used for supporting web keyboard entry.  when input field is focused pass as true to enable web keyboard input.
   * when input field is not focused pass as false to disable web keyboard input.
   * pass undefined to disable web keyboard input entirely.
   */
  isKeyboardFocused?: boolean;
}

const numbers = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '00',
  '0',
  DELETE,
];

const NumberPad = ({
  submitButtonLabel,
  submitButtonTestID,
  handleSubmit,
  handleButtonPress,
  disabled = false,
  submitDisabled = false,
  submitButtonLoading = false,
  showSubmitButton = true,
  analyticsId,
  customAttributes,
  showsDoubleZero = true,
  isKeyboardFocused,
}: NumberPadProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef = useRef<TextInput>(null);

  const handleInputChange = (text: string) => {
    if (text.length < inputValue.length) {
      setInputValue(text);
      handleButtonPress(DELETE);
    } else {
      const filteredText = text
        .split('')
        .filter((char) => {
          return (
            numbers.includes(char) || (char === '0' && numbers.includes('0'))
          );
        })
        .join('');
      setInputValue(filteredText);
      if (filteredText === inputValue) {
        return;
      }
      if (filteredText.length > 0) {
        handleButtonPress(filteredText.charAt(filteredText.length - 1));
      } else {
        handleButtonPress('');
      }
    }
  };

  const handleNumberButtonClick = (number: string) => {
    if (number === DELETE) {
      setInputValue((prev) => prev.slice(0, -1));
    } else {
      setInputValue((prev) => prev + number);
    }
    handleButtonPress(number);
  };

  useEffect(() => {
    if (
      isWeb &&
      !isMobileBrowser &&
      inputRef.current &&
      !disabled &&
      isKeyboardFocused
    ) {
      inputRef.current.focus();
    }
  }, [disabled, inputValue, isKeyboardFocused]);

  return (
    <View>
      {showSubmitButton ? (
        <View style={style.submitButtonContainer}>
          <Button
            label={submitButtonLabel}
            testID={submitButtonTestID}
            onPress={handleSubmit}
            size="LARGE"
            width="FULL"
            type="PRIMARY"
            isLoading={submitButtonLoading}
            isDisabled={disabled || submitDisabled}
            analyticsId={analyticsId}
            customAttributes={customAttributes}
          />
        </View>
      ) : null}
      {isWeb && !isMobileBrowser && isKeyboardFocused ? (
        <TextInput
          ref={inputRef}
          style={style.hiddenInput}
          value={inputValue}
          onChangeText={handleInputChange}
          editable={!disabled}
          testID="hidden-text-input-field"
          onFocus={() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        />
      ) : null}
      <View style={style.dialPad}>
        {numbers.map((number) => {
          return (
            <View key={`wrapper-${number}`} style={style.numberButtonContainer}>
              {showsDoubleZero || number !== '00' ? (
                <NumberPadButton
                  number={number}
                  handleButtonPress={() => handleNumberButtonClick(number)}
                  disabled={disabled}
                />
              ) : null}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  dialPad: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  hiddenInput: {
    height: 0,
    left: -1000,
    opacity: 0,
    position: 'absolute',
    top: -1000,
    width: 0,
  },
  numberButtonContainer: {
    alignItems: 'center',
    marginTop: theme.spacing.s,
    width: '33.3%',
  },
  submitButtonContainer: {
    marginVertical: theme.spacing.s,
  },
});

export default NumberPad;
